import {
  Component,
  Type,
  ComponentFactoryResolver,
  ViewChild,
  OnDestroy,
  ComponentRef,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { InsertionDirective } from './insertion.directive';
import { CpDialogRef } from './dialog.ref';
import { CpDialogConfig } from './dialog.config';

/**
 * Notes:
 *
 * `T`: the component you use when opening the modal
 *
 * `D`: configuration information that you provide when opening the modal
 *
 * `R`: type of the data that is expected to be returned after you click the confirmation button (save, continue ...)
 *
 * You get this through `afterClosed` subscription (i.e. `dialogRef.afterClosed().subscribe((data: R) => {  }))`
 */

/** This is work-around to make our tests pass
    KeyBoardEvent and MouseEvent are global variables in the browser and our test environment doesn't have them 
*/
type TKeyboardEvent = KeyboardEvent;
type TMouseEvent = MouseEvent;
@Component({
  templateUrl: './dialog-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogContainerComponent<T, D, R> implements AfterViewInit, OnDestroy {
  @HostListener('document:keyup', ['$event']) handleKeyUp(event: TKeyboardEvent) {
    if (event.key === 'Escape') {
      this.close(event);
    }
  }
  componentRef: ComponentRef<T>;

  @ViewChild(InsertionDirective)
  insertionPoint: InsertionDirective;

  childComponentType: Type<T>;
  config: CpDialogConfig<D>;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private cdr: ChangeDetectorRef,
    private dialogRef: CpDialogRef<ComponentRef<T>, R>
  ) {}

  ngAfterViewInit() {
    this.loadChildComponent(this.childComponentType);
    this.cdr.detectChanges();
  }

  onOverlayClicked(evt: TMouseEvent) {
    if (this.config.usePOC) {
      // @TODO remove this after POC mode is over
      if (this.config.overlayClose) {
        this.close(evt);
      }
    } else {
      this.close(evt);
    }
  }

  onDialogClicked(evt: TMouseEvent) {
    evt.stopPropagation();
  }

  private loadChildComponent(componentType: Type<T>): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);

    const viewContainerRef = this.insertionPoint.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent(componentFactory);
    this.dialogRef.componentInstance = this.componentRef;
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  accountCenterClassModifier() {
    if (this.config.data['accountCenter']) {
      return 'cp-dialog-overlay--account-center';
    }
    return '';
  }

  accountCenterClassModifierPOC() {
    if (this.config.data['accountCenter']) {
      return 'cp-dialog-overlay__POC--account-center';
    }
    return '';
  }

  close(evt: TMouseEvent | TKeyboardEvent) {
    if (this.config.disableClose) {
      return;
    }
    if (typeof this.config.closeEvent === 'function') {
      this.config.closeEvent(evt);
      return;
    }
    this.dialogRef.close();
  }
}
